import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export type ButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> & {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
};

/**
 * The class list a Button renders, for elements that must NOT be a `<button>`.
 *
 * WHY THIS EXISTS. `<Link><Button/></Link>` puts a `<button>` inside an `<a>`.
 * That is invalid HTML, and browsers do not agree on how to repair it. In
 * practice it gives a keyboard user two stops for one control, and a screen
 * reader announces a nested interactive element inside a link, so the accessible
 * name and role of the thing being operated are both ambiguous. It was in 28
 * places across 15 screens here, always to get button styling on a navigation.
 *
 * A link that navigates should BE a link. This gives it the same look without
 * the nested control, so the markup matches what the element actually does.
 */
export function buttonClasses(opts: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
  className?: string;
} = {}): string {
  const { variant = "secondary", size = "md", full = false, className } = opts;
  return [
    "bht-btn",
    `bht-btn--${variant}`,
    `bht-btn--${size}`,
    full && "bht-btn--full",
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Button — three variants per Design §11 button taxonomy.
 * Primary is amber-on-dark (the only place amber is filled).
 * Touch target ≥ 44px per Design Review §11 accessibility.
 */
export function Button({
  children,
  variant = "secondary",
  size = "md",
  full = false,
  iconLeft,
  iconRight,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      className={buttonClasses({ variant, size, full, className })}
    >
      {iconLeft && <span className="bht-btn__icon">{iconLeft}</span>}
      <span className="bht-btn__label">{children}</span>
      {iconRight && <span className="bht-btn__icon">{iconRight}</span>}
    </button>
  );
}
